import { nanoid } from 'nanoid'

/* @ngInject */
function newAssetService ($location, ConnectorWebService, CONSTANTS, Utils, CustomerCompanyService, SegmentService) {
  const service = {
    getAssetTags,
    getPresignedUploadUrl,
    createAsset,
    getAssetThumbnailUrl,
    checkAssetHasThumb,
    attachThumbnailToAsset,
    startMultipartUpload,
    getMultipartUploadUrl,
    completeMultipartUpload,
    copyAssetUrlToClipboard,
    copyAssetsUrlsToClipboard,
    getAssetHistory,
    updateAsset,
    updateAssetsBatch,
    getAssetDetails,
    downloadAssetsBatch,
    getAssetSegmentation,
    getAssetsSegmentation,
    getAssetsTags,
    getAssetVersions,
    restoreAssetVersion,
    waitAssetThumbnail,
    getCustomFields,
    getAssetCustomFieldsValue
  }
  return service

  function restoreAssetVersion (assetId, version) {
    const url = Utils.fnReplaceStringParams(CONSTANTS.URL_NEW_ASSETS_ID_RESTORE_VERSION_VERSIONID, [assetId, version])
    return ConnectorWebService.put(url)
  }

  function waitAssetThumbnail (params) {
    const {
      assetId,
      quality = 'LOW',
      version = null,
      timeout = 8000
    } = params

    return new Promise((resolve) => {
      const checkIfReady = async () => {
        const hasThumbnail = await service.checkAssetHasThumb(assetId, quality, version)

        if (hasThumbnail) {
          return resolve()
        }

        setTimeout(checkIfReady, timeout)
      }

      checkIfReady()
    })
  }

  function checkAssetHasThumb (id, quality = 'LOW', version = null) {
    let url = Utils.fnReplaceStringParams(CONSTANTS.URL_NEW_ASSETS_ID_HAS_THUMBNAIL, [id])
    url = Utils.fnAddQueryToUrl(url, { quality })

    if (version) {
      url = Utils.fnAddQueryToUrl(url, { assetVersion: version })
    }

    return ConnectorWebService.get(url)
  }

  function getAssetTags (id) {
    const url = Utils.fnReplaceStringParams(CONSTANTS.URL_NEW_ASSETS_TAGS, [id])
    return ConnectorWebService.get(url)
  }

  function getAssetsTags (assetIds) {
    const url = Utils.addQueryStringFromArray(CONSTANTS.URL_NEW_ASSETS_TAGS_BATCH, 'assetsId', assetIds)
    return ConnectorWebService.get(url)
  }

  function getAssetSegmentation (assetId) {
    const url = Utils.fnReplaceStringParams(CONSTANTS.URL_NEW_ASSETS_ID_SEGMENTATION, [assetId])
    return ConnectorWebService.get(url)
  }

  function getAssetsSegmentation (assetIds) {
    const url = Utils.addQueryStringFromArray(CONSTANTS.URL_NEW_ASSETS_SEGMENTATION_BATCH, 'assetsId', assetIds)
    return ConnectorWebService.get(url)
  }

  function getPresignedUploadUrl (s3KeySuffix, extension, fileSize, existentS3Key) {
    /**
      Safari has a problem when many identical requests are made in a short period of time.
      Basically it caches the first request and all the following identical requests are not made. They simply receive the same response as the first request made.

      So in order to fix it, we add a unique id as querystring in the url. This way we create unique requests so Safari cannot cache them.
     */
    const uniqueRequestId = nanoid()
    let url = Utils.fnAddQueryToUrl(CONSTANTS.URL_NEW_ASSETS_PRESIGNED_UPLOAD_URL, { uniqueId: uniqueRequestId })

    if (s3KeySuffix) {
      url = Utils.fnAddQueryToUrl(url, { keySuffix: s3KeySuffix })
    }

    if (fileSize) {
      url = Utils.fnAddQueryToUrl(url, { fileSize })
    }

    if (extension) {
      url = Utils.fnAddQueryToUrl(url, { extension })
    }

    if (existentS3Key) {
      url = Utils.fnAddQueryToUrl(url, { existentS3Key })
    }

    return ConnectorWebService.get(url)
  }

  function createAsset (data, options = {}) {
    const { name, size, contentType, s3Key, folderId, isOrganized, thumbnails } = data
    const { generateThumbnailSync, generateThumbnailInBrowser } = options
    const payload = {
      name,
      size,
      contentType,
      s3Key
    }
    let url = CONSTANTS.URL_NEW_ASSETS

    if (generateThumbnailSync) {
      url = Utils.fnAddQueryToUrl(url, { generateThumbnailSync })
    }

    if (generateThumbnailInBrowser) {
      url = Utils.fnAddQueryToUrl(url, { generateThumbnailInBrowser })
    }

    if (folderId) {
      payload.folderId = folderId
    }

    if (thumbnails?.length) {
      payload.thumbnails = thumbnails
    }

    if (typeof isOrganized === 'boolean') {
      payload.isOrganized = isOrganized
    }

    return ConnectorWebService.post(url, payload)
  }

  function getAssetThumbnailUrl (assetId, quality, options = {}) {
    let url = CONSTANTS.URL_NEW_ASSETS_ID_THUMBNAIL

    if (quality) {
      url = Utils.fnAddQueryToUrl(url, { quality })
    }

    if (options.includeDateAsQueryString) {
      url = Utils.fnAddQueryToUrl(url, { date: new Date().getTime() })
    }

    if (options.version) {
      url = Utils.fnAddQueryToUrl(url, { assetVersion: options.version })
    }

    return Utils.fnReplaceStringParams(url, [assetId])
  }

  function attachThumbnailToAsset (assetId, s3Key) {
    const url = Utils.fnReplaceStringParams(CONSTANTS.URL_NEW_ASSETS_ID_THUMBNAIL, [assetId])

    return ConnectorWebService.post(url, { s3Key })
  }

  function startMultipartUpload (options = {}) {
    let url = CONSTANTS.URL_NEW_ASSETS_START_MULTIPART_UPLOAD

    if (options.fileSize) url = Utils.fnAddQueryToUrl(url, { fileSize: options.fileSize })
    if (options.extension) url = Utils.fnAddQueryToUrl(url, { extension: options.extension })
    if (options.contentType) url = Utils.fnAddQueryToUrl(url, { contentType: options.contentType })
    if (options.s3KeySuffix) url = Utils.fnAddQueryToUrl(url, { keySuffix: options.s3KeySuffix })
    if (options.existentS3Key) url = Utils.fnAddQueryToUrl(url, { existentS3Key: options.existentS3Key })

    return ConnectorWebService.post(url)
  }

  function getMultipartUploadUrl (s3Key, uploadId, partNumber) {
    const url = Utils.fnAddQueryToUrl(CONSTANTS.URL_NEW_ASSETS_MULTIPART_UPLOAD_URL, { key: s3Key, uploadId, partNumber })

    return ConnectorWebService.get(url)
  }

  function completeMultipartUpload (s3Key, uploadId, parts) {
    const url = CONSTANTS.URL_NEW_ASSETS_COMPLETE_MULTIPART_UPLOAD

    return ConnectorWebService.post(url, {
      key: s3Key,
      uploadId,
      parts
    })
  }

  function getUrlForAssetId (assetId) {
    const currentLocation = $location.url()
    const companyBaseUrl = CustomerCompanyService.getCompanyBaseUrl()
    const moduleBaseUrl = `${companyBaseUrl}${currentLocation}`

    return Utils.fnAddQueryToUrl(moduleBaseUrl, {
      previewId: assetId
    })
  }

  function copyAssetUrlToClipboard (assetId) {
    const assetUrl = getUrlForAssetId(assetId)
    createCopyLinkLog(assetId)
    SegmentService.track('file-share-link-copy', { object_type: 'file' })

    return navigator.clipboard.writeText(assetUrl)
  }

  function copyAssetsUrlsToClipboard (assets) {
    const assetsIds = assets.map(asset => asset.id)
    const assetsUrls = []

    assetsIds.forEach(assetId => {
      assetsUrls.push(getUrlForAssetId(assetId))
    })

    createCopyLinksBatchLog(assetsIds)
    SegmentService.track('file-share-link-copy', { object_type: 'file' })

    return navigator.clipboard.writeText(assetsUrls.join(', '))
  }

  function createCopyLinkLog (assetId) {
    const url = Utils.fnReplaceStringParams(CONSTANTS.URL_NEW_ASSETS_ID_COPY_LINK_LOG, [assetId])

    return ConnectorWebService.post(url)
  }

  function createCopyLinksBatchLog (assetsIds) {
    const url = Utils.fnReplaceStringParams(CONSTANTS.URL_NEW_ASSETS_COPY_LINK_BATCH)

    return ConnectorWebService.post(url, { assetsIds })
  }

  function getAssetHistory (assetId, limit, offset) {
    let url = Utils.fnReplaceStringParams(CONSTANTS.URL_NEW_ASSETS_ID_ACTIVITY, [assetId])

    if (limit) {
      url = Utils.fnAddQueryToUrl(url, { $top: limit })
    }

    if (offset) {
      url = Utils.fnAddQueryToUrl(url, { $skip: offset })
    }

    return ConnectorWebService.get(url)
  }

  function getCustomFields (limit, offset, search) {
    let url = Utils.fnReplaceStringParams(CONSTANTS.URL_NEW_ASSETS_CUSTOM_FIELDS)

    if (limit) {
      url = Utils.fnAddQueryToUrl(url, { $top: limit })
    }

    if (offset) {
      url = Utils.fnAddQueryToUrl(url, { $skip: offset })
    }

    if (search) {
      url = Utils.fnAddQueryToUrl(url, { search })
    }

    return ConnectorWebService.get(url)
  }

  function getAssetCustomFieldsValue (assetId) {
    const url = Utils.fnReplaceStringParams(CONSTANTS.URL_NEW_ASSETS_ID_ASSET_CUSTOM_FIELDS_VALUE, [assetId])
    return ConnectorWebService.get(url)
  }

  function updateAsset (assetId, name, tags, users, customFields) {
    const url = Utils.fnReplaceStringParams(CONSTANTS.URL_NEW_ASSETS_ID, [assetId])
    const payload = {
      name
    }

    if (Array.isArray(tags)) {
      payload.assetTagsIds = tags
    }

    if (Array.isArray(users)) {
      payload.usersId = users
    }

    if (Array.isArray(customFields)) {
      payload.customFields = customFields
    }

    return ConnectorWebService.put(url, payload)
  }

  function updateAssetsBatch (assetsId, assetTagsIds, usersId) {
    const url = CONSTANTS.URL_NEW_ASSETS_SETTINGS_BATCH

    const payload = {
      assetsId,
      assetTagsIds,
      usersId
    }

    return ConnectorWebService.put(url, payload)
  }

  function getAssetDetails (assetId, options = {}) {
    let url = Utils.fnReplaceStringParams(CONSTANTS.URL_NEW_ASSETS_ID_DETAILS, [assetId])

    if (options?.formatData) url = Utils.fnAddQueryToUrl(url, { formatData: options.formatData })

    return ConnectorWebService.get(url)
  }

  function downloadAssetsBatch (selectedAssets, downloadModule) {
    const assetsIds = selectedAssets.map(selectedAsset => selectedAsset.id)

    let url = Utils.addQueryStringFromArray(CONSTANTS.URL_NEW_ASSETS_DOWNLOAD_BATCH, 'assetsIds', assetsIds)

    if (downloadModule) {
      url = Utils.fnAddQueryToUrl(url, { downloadModule })
    }

    return Utils.downloadFile(url)
  }

  function getAssetVersions (assetId) {
    const url = Utils.fnReplaceStringParams(CONSTANTS.URL_NEW_ASSETS_ID_VERSIONS, [assetId])

    return ConnectorWebService.get(url)
  }
}

export { newAssetService }
